// The v2 goal commands whose answers the screen waits for (spec §13.3,
// §13.14): a race (its id, and the coach's priority suggestion), the goal,
// and the end of onboarding (the 18+ confirmation). They are not queued like
// calendar edits: the athlete is looking at the form, so a refusal shows
// there. Each still carries an Idempotency-Key, so a retry with the same key
// changes nothing.

import { newKey } from './client';

/** A refused command: `detail` is the backend's reason, ready to show. */
export class CommandRefused extends Error {
  constructor(status, detail) {
    super(detail);
    this.status = status;
    this.detail = detail;
  }
}

const reason = (data, status) => {
  const d = data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;                 // a FastAPI validation error
  return status ? `That didn't save (${status})` : "That didn't save";
};

/** Send one command now (v2API.command) and answer its body; throws CommandRefused, or {network} offline. */
export async function call(api, { method, path, body = {}, key = newKey() }) {
  const res = await api.command({ method, path, body, key });
  if (res.status >= 200 && res.status < 300) return res.data || {};
  throw new CommandRefused(res.status, reason(res.data, res.status));
}

export const actions = {
  /** `priority` null: the coach suggests one (`suggested_priority`), the athlete confirms. */
  addRace: ({ date, title, sport, priority = null, details = {} }) => ({
    method: 'POST', path: '/v2/races',
    body: { date, title, sport, ...(priority ? { priority } : {}), details },
  }),
  confirmPriority: (raceId, priority) => ({ method: 'POST', path: `/v2/races/${raceId}/priority`, body: { priority } }),
  editRace: (raceId, fields) => ({
    method: 'PATCH', path: `/v2/races/${raceId}`,
    body: Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined)),
  }),
  deleteRace: (raceId) => ({ method: 'DELETE', path: `/v2/races/${raceId}`, body: null }),
  setGoal: (goal) => ({ method: 'PUT', path: '/v2/goals', body: goal }),
  /** Onboarding's "Anything else your coach should know?" (signupNote.js), sent before completeOnboarding. */
  saveSignupNote: (text) => ({ method: 'PUT', path: '/v2/onboarding/note', body: { text } }),
  completeOnboarding: (confirmAdult) => ({ method: 'POST', path: '/v2/onboarding/complete', body: { confirm_adult: Boolean(confirmAdult) } }),
};

// v1's race_priority values ↔ the letters v2 speaks.
export const PRIORITY_LETTER = { a_race: 'A', b_race: 'B', c_race: 'C' };
export const PRIORITY_ROW = { A: 'a_race', B: 'b_race', C: 'c_race' };

/**
 * The priority picker after a race is saved without one: the coach's
 * suggestion first (one tap to confirm), then the other two.
 */
export function priorityChoices(suggested) {
  const first = ['A', 'B', 'C'].includes(suggested?.priority) ? suggested.priority : null;
  return ['A', 'B', 'C']
    .sort((a, b) => (a === first ? -1 : b === first ? 1 : 0))
    .map((p) => ({ priority: p, suggested: p === first, label: p === first ? `Make it a ${p} race` : `${p} race` }));
}

/** A race in the v2 store still waits for the athlete to confirm its priority. */
export const needsPriority = (race) => race?.details?.priority_confirmed === false;

/** The suggestion stored on a race saved without a priority. */
export const storedSuggestion = (race) => race?.details?.priority_suggestion || null;

// What a target or a milestone is measured in. Values travel in SI (§13.14): a
// pace is m/s, a distance metres, a duration seconds; the athlete types them
// the way they think of them. The same as mobile's lib/v2/goals.ts.
export const GOAL_METRICS = {
  ftp: { label: 'FTP', unit: 'W', placeholder: '300' },
  threshold_pace: { label: 'Threshold pace', unit: 'per km', placeholder: '4:15' },
  css: { label: 'CSS', unit: 'per 100 m', placeholder: '1:35' },
  distance: { label: 'Distance', unit: 'km', placeholder: '100' },
  duration: { label: 'Duration', unit: 'h:mm', placeholder: '2:00' },
};

export const GOAL_SPORTS = [{ value: 'bike', label: 'Ride' }, { value: 'run', label: 'Run' }, { value: 'swim', label: 'Swim' }];

/** The metrics a kind can carry: a target is a number to reach, a milestone a distance or a time. */
export function metricsFor(kind) {
  if (kind === 'performance_target') return ['ftp', 'threshold_pace', 'css'];
  if (kind === 'milestone') return ['distance', 'duration'];
  return [];
}

/** "m:ss" or "h:mm" → one number of the smaller unit. */
const clock = (text) => {
  const m = /^\s*(\d{1,3}):([0-5]\d)\s*$/.exec(text);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const two = (n) => String(n).padStart(2, '0');

/** What the athlete typed → the SI value PUT /v2/goals takes, or null when it isn't one. */
export function toSI(metric, text) {
  const s = String(text ?? '').trim();
  if (!s) return null;
  if (metric === 'ftp' || metric === 'distance') {
    const n = Number(s.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return null;
    return metric === 'distance' ? Math.round(n * 1000) : Math.round(n);
  }
  const secs = clock(s);
  if (!secs) return null;
  if (metric === 'threshold_pace') return Math.round((1000 / secs) * 1000) / 1000;
  if (metric === 'css') return Math.round((100 / secs) * 1000) / 1000;
  return secs * 60;                                                   // duration: h:mm → s
}

/** The SI value back as the athlete types it. */
export function fromSI(metric, value) {
  if (value == null || !Number.isFinite(value) || value <= 0) return '';
  if (metric === 'ftp') return String(Math.round(value));
  if (metric === 'distance') return String(Math.round(value / 100) / 10);
  if (metric === 'duration') {
    const mins = Math.round(value / 60);
    return `${Math.floor(mins / 60)}:${two(mins % 60)}`;
  }
  const secs = Math.round((metric === 'css' ? 100 : 1000) / value);
  return `${Math.floor(secs / 60)}:${two(secs % 60)}`;
}

/**
 * The goal editor's form → PUT /v2/goals' body, or `{ error }` the athlete
 * can act on. kind '' clears the goal. A performance target needs a metric;
 * its number can wait for a test. A milestone needs a sport to be measured in.
 */
export function goalBody({ kind, metric = '', target = '', by_date = '', sport = '' }) {
  if (!kind) return { body: { kind: null } };
  const by = by_date || null;
  const plain = { kind, metric: null, target: null, by_date: by };
  const metrics = metricsFor(kind);
  if (!metrics.length) return { body: plain };
  const typed = String(target ?? '').trim();
  if (!metrics.includes(metric)) {
    if (kind === 'performance_target') return { error: 'Pick what the target is.' };
    return typed ? { error: 'Pick a distance or a duration.' } : { body: plain };
  }
  const t = toSI(metric, typed);
  if (typed && t == null) {
    const m = GOAL_METRICS[metric];
    return { error: `Enter the ${m.label.toLowerCase()} like ${m.placeholder} (${m.unit}).` };
  }
  if (kind === 'performance_target') return { body: { kind, metric, target: t, by_date: by } };
  if (t == null) return { body: plain };
  if (!sport) return { error: 'Pick the sport.' };
  return { body: { kind, metric, target: t, by_date: by, sport } };
}

/**
 * The goal the editor opens on: /v2/me's `goal` (read at sign-in), unless
 * the stream has carried a goal.upsert since (its seq past /v2/me's
 * last_seq), which is newer. An older backend's /v2/me has no `goal`: then
 * the stream's last goal.upsert, else the committed strategy's goal kind.
 */
export function currentGoal(cal, me) {
  const streamed = cal?.goal_seq != null && cal.goal !== undefined;
  if (streamed && (me?.last_seq == null || cal.goal_seq > me.last_seq)) return cal.goal;
  if (me && 'goal' in me) return me.goal ?? null;
  if (cal?.goal) return cal.goal;
  const kind = cal?.strategy?.goals?.find((g) => g.kind && g.kind !== 'race')?.kind;
  return kind ? { goal_kind: kind } : null;
}

/**
 * goal.upsert's payload (user_profiles' goal_* columns) → the editor's form.
 * A metric from before the editor had fixed choices ("FTP (W)") opens unset,
 * with its number, which was in no known unit, left out.
 */
export function goalForm(goal) {
  const kind = goal?.goal_kind || '';
  const metric = metricsFor(kind).includes(goal?.goal_metric) ? goal.goal_metric : '';
  return {
    kind, metric,
    target: metric ? fromSI(metric, goal?.goal_target) : '',
    by_date: goal?.goal_by_date ? String(goal.goal_by_date).slice(0, 10) : '',
    sport: goal?.goal_sport || '',
  };
}

/**
 * One Idempotency-Key per thing a form saves, kept across retries and
 * remounts (sessionStorage when it works, memory otherwise): onboarding's
 * Back then Continue sends the same race again with the same key, so the
 * backend saves it once (aithlo-frontend#64).
 */
const KEYS_STORE = 'aithlo_v2_form_keys';
const memoryKeys = {};
export function stableKey(scope, signature, storage = globalThis.sessionStorage) {
  const id = `${scope}:${signature}`;
  let keys = memoryKeys;
  try { keys = { ...memoryKeys, ...JSON.parse(storage?.getItem(KEYS_STORE) || '{}') }; } catch { /* memory only */ }
  if (!keys[id]) {
    keys[id] = newKey();
    memoryKeys[id] = keys[id];
    try { storage?.setItem(KEYS_STORE, JSON.stringify(keys)); } catch { /* memory only */ }
  }
  return keys[id];
}

/** What makes two onboarding races the same race: its day and its name. */
export const raceSignature = ({ date, title }) => `${date}|${String(title || '').trim().toLowerCase()}`;
